// 告诉客服系统（LibreDesk）用户当前在看哪家酒店 / 餐厅 / 品牌。
//
// GET /auth/me?entityType=&entityId= 由 api 做权限校验后，在服务端把
// {entityType, entityId, entityName} 写到 LibreDesk 联系人上；AI 客服和转人工工单都读它。
// 几条约束（都是踩过或读源码确认的）：
//
// 1) LibreDesk 只合并、从不清空联系人属性。不报 = 留着上一家店，所以没有当前实体时要显式
//    报 NONE，api 会写三个 null 把旧值清掉。不带参数的 /auth/me（chat、移动端）则什么都不动。
// 2) 实体只看 store、不看路由：切菜单不换店，没必要每点一次菜单就打一次 LibreDesk。
//    品牌模式优先判断 —— 进品牌模式时 isHotel / currentHotelId 都不会被清。
// 3) 只报 /auth/entities 列表里有的实体（持久化的 id 可能已经失效），和 api 的权限口径一致。
// 4) 上报必须串行、以最后一次为准：两个请求同时在途时，后发的未必后写。
// 5) 属性按联系人（email）存一份，多个标签页 / 设备谁最后报谁说了算，所以标签页重新可见时要强制重报。
// 6) 不知道（/auth/entities 没拉到）≠ 没有：不知道时不带参数，api 不动联系人上的旧值；
//    报 NONE 会把最后一个正确值清掉。约定：null = 没有实体，undefined = 不知道。

/** 当前实体；未登录或列表里对不上时返回 null（→ 报 NONE）。 */
export function currentEntity(rootState) {
  const auth = (rootState && rootState.auth) || {};
  if (!auth.token) return null;

  const brandName = auth.selectedBrandName;
  if (brandName) {
    // 持久化的品牌名可能已不在列表里：宁可报 NONE，也不回退成品牌模式下残留的酒店。
    return (auth.brands || []).some((b) => b && b.name === brandName)
      ? { entityType: "BRAND", entityId: brandName }
      : null;
  }

  const hotel = rootState.hotel || {};
  if (hotel.isHotel) {
    const id = hotel.currentHotelId;
    return id && (hotel.hotelList || []).some((h) => h && h.hotelId === id)
      ? { entityType: "HOTEL", entityId: id }
      : null;
  }

  const restaurant = rootState.restaurant || {};
  const rid = restaurant.selectedRestaurantId;
  return rid && (restaurant.restaurantList || []).some((r) => r && r.restaurantId === rid)
    ? { entityType: "RESTAURANT", entityId: rid }
    : null;
}

/** /auth/me 的查询参数。null 值 axios 会直接丢掉，所以"没有实体"必须是字符串 NONE；不知道则不带参数。 */
export function entityParams(entity) {
  if (entity === undefined) return undefined;
  return entity
    ? { entityType: entity.entityType, entityId: entity.entityId }
    : { entityType: "NONE" };
}

export function entityKey(entity) {
  if (entity === undefined) return "UNKNOWN";
  return entity ? `${entity.entityType}:${entity.entityId}` : "NONE";
}

/**
 * 串行上报器：同一时间只有一个请求在途；在途期间来的新实体只保留最新一个，等前一个结束再发；
 * 和上一次请求的实体相同就不发（force 时照发：别的标签页可能已经把服务端的值改掉了）。
 * send(params, isFirst) 返回 Promise，失败不影响后续上报。
 */
export function createEntityReporter(send) {
  let requestedKey = null; // 最近一次决定要报的实体（在途或已发）
  let pending = null;
  let inFlight = false;
  let sentAny = false;

  function pump() {
    if (inFlight || !pending) return;
    const next = pending;
    pending = null;
    inFlight = true;
    const isFirst = !sentAny;
    sentAny = true;
    Promise.resolve()
      .then(() => send(next.params, isFirst))
      .catch(() => {})
      .then(() => {
        inFlight = false;
        pump();
      });
  }

  return {
    report(entity, { force = false } = {}) {
      const key = entityKey(entity);
      if (key === requestedKey && !force) return false;
      requestedKey = key;
      pending = { params: entityParams(entity) };
      pump();
      return true;
    },
    get started() {
      return sentAny;
    },
  };
}
